"use client";

import { Avatar } from "@package/subframe/components/Avatar";
import { DropdownMenu } from "@package/subframe/components/DropdownMenu";
import {
  FeatherDownload,
  FeatherLogOut,
  FeatherRefreshCw,
  FeatherTrash2,
  FeatherUser,
} from "@subframe/core";
import { Link } from "@tanstack/react-router";
import { toast } from "sonner";

import { env } from "@/config/env";
import { useAuth } from "@/contexts/AuthContext";
import { useNursery } from "@/contexts/NurseryContext";
import { trackSessionEnded } from "@/lib/analytics";
import { signOut } from "@/lib/auth";
import { clearAllOfflineData } from "@/lib/offline/db";

interface UserMenuProps {
  user: {
    name: string;
    email: string;
    avatar?: string;
  };
}

const isProduction = env.VITE_APP_ENV === "production";

export function UserMenu(_props: UserMenuProps) {
  const { user, nurseryAccess } = useAuth();
  const { selectedNurseryId } = useNursery();
  // A profile is read through a nursery the person belongs to. Admins with no
  // membership at the selected nursery would only get "not available".
  // Hidden while the People Hub is switched off.
  const canOpenOwnProfile =
    env.VITE_PEOPLE_HUB_ENABLED &&
    !!user &&
    nurseryAccess.some((access) => access.nurseryId === selectedNurseryId);

  const handleSignOut = async () => {
    // Track session end with duration
    const sessionStartTime = sessionStorage.getItem("session_start_time");
    const sessionDurationSeconds = sessionStartTime
      ? Math.floor((Date.now() - parseInt(sessionStartTime, 10)) / 1000)
      : 0;
    trackSessionEnded("manual", sessionDurationSeconds);
    sessionStorage.removeItem("session_start_time");

    // Clear offline data to prevent data leakage between users
    await clearAllOfflineData();
    await signOut();
    window.location.href = "/login";
  };

  const handleClearOfflineData = async () => {
    try {
      // The one caller that genuinely means "wipe everything": a non-production
      // debug action, invoked deliberately. Logout paths keep unsynced work.
      await clearAllOfflineData({ keepUnsyncedWork: false });
      window.location.reload();
      toast.success("Offline data cleared");
    } catch (error) {
      console.error("Failed to clear offline data:", error);
      toast.error("Failed to clear offline data");
    }
  };

  return (
    <DropdownMenu>
      {canOpenOwnProfile && (
        <Link
          to="/people/$userId"
          params={{ userId: user.id }}
          className="w-full"
        >
          <DropdownMenu.DropdownItem icon={<FeatherUser />}>
            My profile
          </DropdownMenu.DropdownItem>
        </Link>
      )}
      <Link to="/install" className="w-full">
        <DropdownMenu.DropdownItem icon={<FeatherDownload />}>
          Install App
        </DropdownMenu.DropdownItem>
      </Link>
      <Link to="/sync-status" className="w-full">
        <DropdownMenu.DropdownItem icon={<FeatherRefreshCw />}>
          Sync Status
        </DropdownMenu.DropdownItem>
      </Link>
      {!isProduction && (
        <DropdownMenu.DropdownItem
          icon={<FeatherTrash2 />}
          onSelect={handleClearOfflineData}
        >
          Clear Offline Data
        </DropdownMenu.DropdownItem>
      )}
      <DropdownMenu.DropdownDivider />
      <DropdownMenu.DropdownItem
        icon={<FeatherLogOut />}
        onSelect={handleSignOut}
      >
        Log out
      </DropdownMenu.DropdownItem>
    </DropdownMenu>
  );
}

export function UserMenuTrigger({ user }: UserMenuProps) {
  const initials =
    user.name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) || "U";

  return (
    <Avatar
      className="mobile:h-11 mobile:w-11 mobile:flex-none cursor-pointer"
      image={user.avatar}
      data-testid="user-menu-trigger"
    >
      {initials}
    </Avatar>
  );
}
